import { useState } from "react";
import axios from "axios";
import { Loader2, PauseCircle, PlayCircle, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { API } from "@/modules/collector_v2/lib/api";

/**
 * CollectorControlButtons — Modo único PRODUCCIÓN (cash-only).
 *
 * Controles disponibles:
 *  - Iniciar Captura  → POST /api/v2/collector/control/start
 *  - Pausar           → POST /api/v2/collector/control/pause
 *  - Reanudar         → POST /api/v2/collector/control/resume
 *  - Detener          → POST /api/v2/collector/control/stop
 */
export function CollectorControlButtons({ status, onChange }) {
  const [busy, setBusy] = useState(null); // null | 'start' | 'pause' | 'resume' | 'stop'

  const isRunning = status?.controller?.is_running && status?.runtime_state === "running";
  const isPaused = status?.runtime_state === "paused";
  const canStart = !isRunning && !isPaused;

  const callControl = async (action, body) => {
    setBusy(action);
    try {
      const url = `${API}/v2/collector/control/${action}`;
      const response = await axios.post(url, body || {});
      const data = response.data || {};
      toast.success(`Collector · ${action}`, {
        description: data.runtime_state || "ok",
      });
      onChange?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.error || detail?.hint || err.message;
      toast.error(`Collector · ${action} falló`, { description: String(msg) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {canStart && (
        <Button
          data-testid="collector-start-button"
          variant="default"
          onClick={() =>
            callControl("start", { cycle_interval_seconds: 30 })
          }
          disabled={busy !== null}
        >
          {busy === "start" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <PlayCircle className="h-4 w-4" />
          )}
          Iniciar Captura
        </Button>
      )}

      {isRunning && (
        <Button
          data-testid="collector-pause-button"
          variant="outline"
          onClick={() => callControl("pause")}
          disabled={busy !== null}
        >
          {busy === "pause" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <PauseCircle className="h-4 w-4" />
          )}
          Pausar
        </Button>
      )}

      {isPaused && (
        <Button
          data-testid="collector-resume-button"
          variant="default"
          onClick={() => callControl("resume")}
          disabled={busy !== null}
        >
          {busy === "resume" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <PlayCircle className="h-4 w-4" />
          )}
          Reanudar
        </Button>
      )}

      {(isRunning || isPaused) && (
        <Button
          data-testid="collector-stop-button"
          variant="outline"
          onClick={() =>
            callControl("stop", { reason: "manual_stop_from_ui" })
          }
          disabled={busy !== null}
        >
          {busy === "stop" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Power className="h-4 w-4" />
          )}
          Detener
        </Button>
      )}
    </>
  );
}
